import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import { FlexyooNotificationsService, WhiteLabelService } from '@flexyoo/common';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { hasNotificationRedirect } from '@flexyoo/shared/functions';
import { SidebarNotification } from '@flexyoo/shared/models';
import { FlexyooLocalizedNamePipe } from '@flexyoo/shared/pipes';
import { UserNotificationsService } from '@flexyoo/shared/services';
import { userNotificationsSelectors } from '@flexyoo/user/store';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { shareReplay, takeUntil } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { NgOnDestroy } from '../../../shared/ng-on-destroy.service';
import { HrEmployeeListFiltersService } from '../../hr-employee-list/hr-employee-list-filters.service';
import { HrTransactionsFiltersService } from '../../hr-transactions/services/hr-transactions-filters.service';
import { HrAlertsBlockComponent } from '../hr-alerts-block/hr-alerts-block.component';
import { NotificationsSidebarService } from '../services/notifications-sidebar.service';

@Component({
  selector: 'app-notifications-sidebar',
  templateUrl: './notifications-sidebar.component.html',
  styleUrl: './notifications-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NgOnDestroy],
  imports: [
    AsyncPipe,
    HrAlertsBlockComponent,
    TranslateModule,
    MatIcon,
    FlexyooLocalizedNamePipe,
    DatePipe,
    MatCheckbox,
    MatButton,
    FlexyooLetDirective,
    MatTooltip,
    MatIconButton,
    ReactiveFormsModule,
  ],
})
export class NotificationsSidebarComponent implements OnInit {
  @Input() public isHrUser = false;

  private readonly onDestroy$ = inject(NgOnDestroy, { self: true });
  private readonly store = inject(Store);
  private readonly userNotificationsService = inject(UserNotificationsService);
  private readonly notificationsSidebarService = inject(NotificationsSidebarService);
  protected readonly translateService = inject(TranslateService);
  protected readonly wls = inject(WhiteLabelService);
  protected readonly hasRedirect = hasNotificationRedirect;
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly hrEmployeeListFiltersService = inject(HrEmployeeListFiltersService);
  private readonly hrTransactionsFiltersService = inject(HrTransactionsFiltersService);
  private readonly flexyooNotificationsService = inject(FlexyooNotificationsService);

  protected readonly isSilentModeEnabled$ = this.flexyooNotificationsService.isSilentModeEnabled$;

  protected readonly isImpersonated$ = this.authService.currentUserSubject.pipe(
    map((user) => !!user?.Impersonated),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected readonly hrAlerts$ = this.store.select(userNotificationsSelectors.selectHrAlerts);
  protected readonly hrAlertsCount$ = this.store.select(
    userNotificationsSelectors.selectHrAlertsCount
  );

  protected readonly notifications$ = this.store.select(
    userNotificationsSelectors.selectUserSidebarNotifications
  );

  protected selectedNotifications = new Set<number>();

  protected textTranslateSelector = (notif: SidebarNotification) => ({
    en: [notif.HeaderEn, notif.TextEn].filter((v) => !!v).join('. '),
    fr: [notif.HeaderFr, notif.TextFr].filter((v) => !!v).join('. '),
    nl: [notif.HeaderNl, notif.TextNl].filter((v) => !!v).join('. '),
  });

  public ngOnInit() {
    this.notifications$.pipe(takeUntil(this.onDestroy$)).subscribe((notifications) => {
      this.selectedNotifications.clear();
      notifications.forEach((notification) => {
        this.selectedNotifications.add(notification.Id);
      });
    });
  }

  protected toggleSilentMode(currVal: boolean): void {
    this.flexyooNotificationsService.toggleSilentMode(!currVal);
  }

  protected selectNotification(ev: MatCheckboxChange, notif: SidebarNotification) {
    ev.checked
      ? this.selectedNotifications.add(notif.Id)
      : this.selectedNotifications.delete(notif.Id);
  }

  protected readNotifications(): void {
    if (!this.selectedNotifications.size) {
      return;
    }

    const ids = Array.from(this.selectedNotifications.values());
    this.userNotificationsService.closeNotificationAndReload(ids).subscribe();
  }

  protected proceedToList(): void {
    this.router.navigateByUrl(
      this.isHrUser ? '/hr/notifications-center' : '/employee-notifications-center'
    );
    this.notificationsSidebarService.toggle(false);
  }

  protected navigateByUrl(item: SidebarNotification): void {
    this.userNotificationsService.proceedToRedirectPage(
      {
        id: item.Id,
        type: item.Type,
        textEn: item.TextEn,
        employeeLastName: item.EmployeeLastName ?? '',
        redirectPage: item.RedirectPage,
        employeeBenefitId: item.EmployeeBenefitId,
        benefitCategory: item.BenefitCategory,
      },
      this.hrEmployeeListFiltersService,
      this.hrTransactionsFiltersService,
      this.authService.getCurrentUser()?.Role === 'HR',
      true
    );
  }
}
